import { useNavigation } from "@react-navigation/native";
import { Calendar, Link } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import { XStack, YStack } from "tamagui";
import { PrimaryCheckText } from "../../../core/components/PrimaryCheck";
import { getDayJsFromDate } from "../../../core/dayJs";
import { Body } from "../../../ui/Body";
import { InfoRow, type InfoRowItemProps } from "../../../ui/InfoRow";
import { Label } from "../../../ui/Label";
import { Subtext } from "../../../ui/Subtext";
import { BusinessModuleBlock } from "../ui/BusinessBlock";
import { BusinessModuleIcon } from "../ui/BusinessModuleIcon";
import { BusinessDateDue } from "./BusinessLateDate";
import type BusinessReminderModuleModel from "./BusinessReminderModuleModel";
import { BusinessReminderUI } from "./BusinessReminderUI";

export const BusinessReminderModuleUI = observer(
  ({ businessModule }: { businessModule: BusinessReminderModuleModel }) => {
    return <BusinessReminderUI businessModule={businessModule} />;
  },
);

export const ReminderModuleBlockUI = observer(
  ({ businessModule }: { businessModule: BusinessReminderModuleModel }) => {
    const { t } = useTranslation();
    const isCompleted = businessModule.isCompletedByUserOrTeam;

    let timeRow: JSX.Element;
    if (isCompleted) {
      timeRow = <PrimaryCheckText text={t("business:completed")} />;
    } else {
      timeRow = <BusinessDateDue dateDue={businessModule.calculatedDate} />;
    }

    return (
      <BusinessModuleBlock
        businessModule={businessModule}
        infoRowItems={businessModule.infoRowItems}
      >
        {timeRow}
      </BusinessModuleBlock>
    );
  },
);

export const ReminderModuleFeedItemUI = observer(
  ({ businessModule }: { businessModule: BusinessReminderModuleModel }) => {
    const navigation = useNavigation();
    const { t } = useTranslation();

    const navigateToModule = React.useCallback(() => {
      navigation.navigate("BusinessTasksModule", {
        businessUid: businessModule.businessPlan.uid,
        businessModuleUid: businessModule.uid,
      });
    }, [businessModule, navigation]);

    const infoRowItems = React.useMemo<InfoRowItemProps[]>(() => {
      const isCompleted = businessModule.isCompletedByUserOrTeam;

      let timeRow: JSX.Element;
      if (isCompleted) {
        timeRow = <PrimaryCheckText text={t("business:completed")} />;
      } else {
        timeRow = <BusinessDateDue dateDue={businessModule.calculatedDate} />;
      }

      const items: InfoRowItemProps[] = [];

      items.push({
        icon: Calendar,
        text: timeRow,
      });

      items.push({
        text: (
          <Subtext
            onPress={(e) => {
              e.stopPropagation();
              navigation.navigate("BusinessFeedItem", {
                businessUid: businessModule.businessPlan.uid,
                itemUid: businessModule.data.feedItemUid,
              });
            }}
          >
            <Link color={"$muted"} size={"$size.small"} />
          </Subtext>
        ),
      });

      return items;
    }, [t, businessModule, navigation]);

    const feedInfo = businessModule.feedInfo;

    const lastUser = feedInfo.lastCompleted
      ? businessModule.businessPlan.data.userInfo[
          feedInfo.lastCompleted.userUid
        ]
      : null;
    return (
      <YStack>
        <XStack
          gap={"$space.medium"}
          alignItems={"center"}
          paddingVertical={"$large"}
          paddingBottom={"$medium"}
          paddingHorizontal={"$larger"}
          borderBottomColor={"$divider"}
          borderBottomWidth={1}
          cursor={"pointer"}
          onPress={(e) => {
            e.stopPropagation();
            navigateToModule();
          }}
        >
          <BusinessModuleIcon
            size={50}
            key={`${businessModule.data.emoji}`}
            businessModule={businessModule}
          />
          <YStack gap={"$small"}>
            <Label>{businessModule.data.title}</Label>

            <InfoRow items={infoRowItems} />
          </YStack>
        </XStack>

        <YStack
          paddingHorizontal={"$larger"}
          paddingVertical={"$large"}
          gap={"$medium"}
        >
          {lastUser && feedInfo.lastCompleted && (
            <Body>
              <Body fontWeight={"bold"}>{lastUser.displayName}</Body>{" "}
              {t("business:completed_this")}{" "}
              {getDayJsFromDate(new Date(feedInfo.lastCompleted.completedAt))}.
            </Body>
          )}
          <Body muted>
            {t("business:completed_this_total", {
              count: feedInfo.totalCompleted,
            })}
            {feedInfo.totalSkipped > 0 &&
              " " +
                t("business:skipped_this_total", {
                  count: feedInfo.totalSkipped,
                })}
          </Body>
        </YStack>
      </YStack>
    );
  },
);
